import React, { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import qs from 'query-string';
import { getDayEnd, getDayStart } from 'utils';

import {
  ArchivedSportsbookProvider,
  Bus,
  SelectedResultsEventProvider,
  SessionStorageManager,
  SiteConfigManager,
  useArchivedEventState,
  useArchivedSportsbookState,
} from '@atlas/core';
import { SportsbookRouter } from '@atlas/routing';

import { ARCHIVED_EVENT_EXTRA_DATA_STORAGE_KEY } from 'common/constants';
import SportsbookMaintenanceContainer from 'components/MaintenanceContainer/SportsbookMaintenanceContainer';
import DetailsPage from 'src/components/Pages/Results/DetailsPage';
import AltenarSportsbook from 'src/components/Sportsbook/altenar/AltenarSportsbook';
import {
  getConfiguredSportsbookProvider,
  SPORTSBOOK_PROVIDERS,
} from 'src/components/Sportsbook/providerRegistry';

import ResultsView from './ResultsView';

const ResultsEventViewProvider = () => {
  const { pathname, search } = useLocation();
  const { push: historyPush } = useHistory();
  const { selectedEventUrlName, updateArchivedSportsbookState } = useArchivedSportsbookState();
  const eventState = useArchivedEventState();
  const { error, id, updateArchivedEventState } = eventState;
  const eventUrlId = qs.parse(search).r;

  React.useEffect(() => {
    if (!id) {
      return;
    }

    Bus.emit('ui:scroll:content', {
      behavior: 'auto',
      scrollTo: 0,
    });
  }, [id]);

  React.useEffect(() => {
    if (!eventUrlId) {
      return;
    }

    if (error) {
      historyPush(pathname);
      updateArchivedSportsbookState({
        selectedEventId: null,
        selectedEventUrlName: null,
      });
    } else if (!id) {
      const extraEventData = SessionStorageManager.getItem(
        `${ARCHIVED_EVENT_EXTRA_DATA_STORAGE_KEY}_${eventUrlId}`,
      );
      if (extraEventData) updateArchivedEventState(JSON.parse(extraEventData));
      updateArchivedSportsbookState({
        selectedEventId: +eventUrlId,
        selectedEventUrlName: +eventUrlId,
      });
    }
  }, [
    error,
    eventUrlId,
    historyPush,
    id,
    pathname,
    updateArchivedEventState,
    updateArchivedSportsbookState,
  ]);

  const isAltenarProvider = getConfiguredSportsbookProvider() === SPORTSBOOK_PROVIDERS.ALTENAR;
  if (isAltenarProvider) {
    const altenarConfig =
      SiteConfigManager.getConfig('config.config.sportsbookIntegrator.altenar') || {};
    const integration = SiteConfigManager.getConfig(
      'config.config.sportsbookIntegrator.altenarIntegrationName',
    );
    return <AltenarSportsbook culture={altenarConfig.culture} integration={integration} />;
  }

  if (eventUrlId && ((selectedEventUrlName && selectedEventUrlName === id) || !id)) {
    return <DetailsPage />;
  }

  return (
    <div className="layout-to-scroll">
      <ResultsView />
    </div>
  );
};

function getInitialDateFilters() {
  const date = dayjs();
  const fromDate = getDayStart(date);
  const toDate = getDayEnd(date);

  return {
    fromDate,
    toDate,
  };
}

const initialDateFilters = getInitialDateFilters();

const ArchivedSportsbook = () => {
  const archivedSportsbookParams = useMemo(
    () => ({
      currentDate: true,
      fromDate: initialDateFilters.fromDate,
      toDate: initialDateFilters.toDate,
    }),
    [],
  );

  return (
    <SportsbookMaintenanceContainer>
      <SportsbookRouter>
        <ArchivedSportsbookProvider params={archivedSportsbookParams}>
          <SelectedResultsEventProvider>
            <ResultsEventViewProvider />
          </SelectedResultsEventProvider>
        </ArchivedSportsbookProvider>
      </SportsbookRouter>
    </SportsbookMaintenanceContainer>
  );
};

export default ArchivedSportsbook;
